import React, { useState } from 'react';
import { MessageSquareWarning } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import { cn } from 'lib/utils';
import { useAttemptQuestionReports } from '../useAttemptQuestionReports';
import { ReportQuestionDialog } from './ReportQuestionDialog';

export interface ReportQuestionButtonProps {
  attemptId: string;
  questionId: string;
  className?: string;
  /** Shows the words beside the icon, for a place where this is the only action. Otherwise they are screen-reader text. */
  withLabel?: boolean;
}

/**
 * A small secondary control beside Flag: opens the report dialog for one question. It shows only once the
 * API has said the author allows reports for this attempt's test, and then reflects an earlier report. The
 * caller decides the rest (no attempt id in preview mode).
 */
export function ReportQuestionButton({ attemptId, questionId, className, withLabel = false }: ReportQuestionButtonProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { data } = useAttemptQuestionReports(attemptId);
  if (data?.enabled !== true) return null;

  const existing = data.reports.find((report) => report.questionId === questionId);
  const label = existing ? t('question_reports.reported') : t('question_reports.report');

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        title={label}
        aria-label={label}
        className={cn(
          'border-error-300 bg-error-50 text-error-700 hover:border-error-400 hover:bg-error-100 hover:text-error-700 dark:border-error-800 dark:bg-error-900/30 dark:text-error-400 dark:hover:bg-error-900/50 dark:hover:text-error-400',
          withLabel ? 'h-9 shrink-0 gap-1.5 px-3.5' : 'h-10 w-10 shrink-0 p-0 lg:h-9 lg:w-9',
          className,
        )}
        onClick={() => setOpen(true)}
      >
        <MessageSquareWarning className="h-4 w-4" aria-hidden />
        <span className={cn(!withLabel && 'sr-only')}>{label}</span>
      </Button>
      <ReportQuestionDialog
        open={open}
        attemptId={attemptId}
        questionId={questionId}
        initial={existing}
        onClose={() => setOpen(false)}
      />
    </>
  );
}
